import React, { useEffect, useMemo, useState } from "react";
import { SupportedLocale } from "../lib/content";

type LandingKey =
  | "pogryzienie-czlowieka"
  | "pogryzienie-zwierzecia"
  | "rekojmia-zwierze"
  | "spor-o-psa-po-rozstaniu"
  | "blad-weterynaryjny";

type Props = {
  locale: SupportedLocale;
  slug: string;
};

type LandingCopy = {
  title: string;
  intro: string[];
  situationsTitle: string;
  situations: string[];
  howTitle: string;
  howSteps: { title: string; desc: string }[];
  pricingTitle: string;
  pricingLines: string[];
  sidesNoteTitle: string;
  sidesNote: string;

  related?: { title: string; href: string };

  faqTitle: string;
  faq: { q: string; a: string }[];
  ctaLabel: string;
};

const CTA_HREF = "/#kontakt";
const SECTION_TIGHT: React.CSSProperties = { paddingTop: 28, paddingBottom: 28 };

const CostsTabs: React.FC<{ items: { title: string; text: string }[] }> = ({ items }) => {
  const [active, setActive] = useState(0);

  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10, marginBottom: 16 }}>
        {items.map((it, idx) => {
          const isActive = idx === active;
          return (
            <button
              key={it.title}
              type="button"
              onClick={() => setActive(idx)}
              className="chip"
              style={{
                cursor: "pointer",
                background: "#fff",
                color: "#111",
                borderRadius: 999,
                padding: "10px 14px",
                border: isActive ? "2px solid #0C3831" : "1px solid rgba(0,0,0,0.18)",
                opacity: 1,
              }}
              aria-current={isActive ? "true" : undefined}
            >
              {it.title}
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 0, padding: 0 }}>
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>{items[active]?.title}</h3>
        <p className="muted" style={{ margin: 0 }}>
          {items[active]?.text}
        </p>
      </div>
    </div>
  );
};

const isLandingKey = (k: string, base: Record<LandingKey, LandingCopy>): k is LandingKey => {
  return k in base;
};

export const ZwierzetaCywilLanding: React.FC<Props> = ({ locale, slug }) => {
  const copy: LandingCopy = useMemo(() => {
    // ====== WSPÓLNE "JAK POMAGAMY" — Twoja poprawna wersja (3 kroki) + tłumaczenia 1:1 ======
    const commonHowStepsPL = [
      {
        title: "Konsultacja wstępna",
        desc:
          "Ustalimy stan faktyczny, uporządkujemy dowody, określimy, czy i jakie roszczenia Tobie przysługują, oszacujemy wartość roszczeń oraz przedstawimy strategię działania.",
      },
      {
        title: "Postępowanie przedsądowe",
        desc:
          "Podejmiemy wszystkie działania zmierzające do jak najszybszego uzyskania dla Ciebie należnych świadczeń (nawet o względnie niewielkiej wartości), w sposób zasadny pod względem ekonomicznym. W tym celu negocjujemy z drugą stroną sporu, uczestniczymy w mediacjach jako Twój pełnomocnik, zgłaszamy szkodę do ubezpieczyciela, a gdy zawiodą pozasądowe sposoby rozstrzygania sporów, składamy wezwanie do zapłaty (pozasądowe sposoby rozstrzygania sporów nie są obowiązkowe, ale stanowczo je polecamy).",
      },
      {
        title: "Postępowanie sądowe",
        desc:
          "Przygotujemy pozew albo odpowiedź na pozew, wniosek o zabezpieczenie (jeżeli jest zasadny), poprowadzimy Twoją sprawę w sądzie aż do jej prawomocnego rozstrzygnięcia.",
      },
    ];

    const commonHowStepsEN = [
      {
        title: "Initial consultation",
        desc:
          "We will establish the facts, organise the evidence, determine whether and which claims you have, estimate the value of the claims and present a strategy.",
      },
      {
        title: "Pre-litigation stage",
        desc:
          "We will take all steps aimed at obtaining what is due to you as quickly as possible (even if the amounts are relatively small), in an economically justified way. To do so, we negotiate with the other side, participate in mediation as your attorney, notify the claim to the insurer and, if out-of-court methods fail, we send a formal demand for payment (out-of-court methods are not mandatory, but we strongly recommend them).",
      },
      {
        title: "Court proceedings",
        desc:
          "We will prepare the statement of claim or defence, a motion for interim measures (if justified) and conduct your case in court until it becomes final.",
      },
    ];

    const commonHowStepsES = [
      {
        title: "Consulta inicial",
        desc:
          "Determinaremos los hechos, ordenaremos las pruebas, estableceremos si y qué reclamaciones te corresponden, estimaremos su valor y presentaremos una estrategia de actuación.",
      },
      {
        title: "Fase extrajudicial",
        desc:
          "Realizaremos todas las actuaciones para obtener lo que te corresponde lo antes posible (incluso si se trata de importes relativamente pequeños), de manera económicamente razonable. Para ello negociamos con la otra parte, participamos en mediación como tu representante, notificamos el siniestro a la aseguradora y, si fracasan las vías extrajudiciales, enviamos un requerimiento de pago (las vías extrajudiciales no son obligatorias, pero las recomendamos firmemente).",
      },
      {
        title: "Procedimiento judicial",
        desc:
          "Prepararemos la demanda o la contestación, la solicitud de medidas cautelares (si procede) y llevaremos tu asunto ante el tribunal hasta su resolución firme.",
      },
    ];

    const commonHowSteps =
      locale === "en" ? commonHowStepsEN : locale === "es" ? commonHowStepsES : commonHowStepsPL;

    // ====== BAZA PL (Twoje aktualne teksty z pliku) ======
    const basePL: Record<LandingKey, LandingCopy> = {
      "pogryzienie-czlowieka": {
        title: "Szkoda na osobie wyrządzona przez zwierzę",
        intro: [
          "Konsekwencje prawne ataku zwierzęcia na człowieka są złożone. Nie chodzi tylko o odszkodowanie za uszczerbek na zdrowiu fizycznym poszkodowanego. Osoba zaatakowana przez zwierzę może również żądać zadośćuczynienia za straty moralne, objawiające się np. w lęku przed zwierzętami. Na tym jednak zakres potencjalnych roszczeń wcale się nie kończy. Poszkodowany, który stał się inwalidą, może żądać zapłaty sumy potrzebnej do przygotowania do nowego zawodu. W skrajnych przypadkach, jak częściowa lub całkowita utrata zdolności do pracy zarobkowej, poszkodowany może żądać renty. Przychodzimy Państwu z pomocą.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "Jak pomagamy?",
        howSteps: commonHowSteps,
        pricingTitle: "Koszty pomocy",
        pricingLines: [
          "Konsultacja wstępna: od 500 zł brutto",
          "Postępowanie przedsądowe: od 1000 zł brutto (konsultacja wstępna w cenie), udział w mediacji dodatkowo płatny, od 300 zł brutto za sesję",
          "Postępowanie sądowe: wycena indywidualna",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: { title: "", href: "" },
        faqTitle: "FAQ",
        faq: [
          {
            q: "Kto odpowiada za pogryzienie człowieka przez psa i czy zawsze jest to właściciel?",
            a: "Odpowiada osoba, która zwierzę chowa lub się nim posługuje, nie zawsze jest to właściciel (vide: art. 431 kc).",
          },
          {
            q: "Czy zachowanie psa albo poszkodowanego człowieka wpływa na zakres odpowiedzialności?",
            a: "Tak — przebieg zdarzenia (np. samowolne wejście na teren posesji) wpływa na zakres odpowiedzialności oraz na zakres roszczeń. Każdą sprawę ocenia się na podstawie dowodów.",
          },
          {
            q: "Jakich roszczeń mogę dochodzić po pogryzieniu mnie przez psa?",
            a: "W grę wchodzi m.in. zwrot kosztów leczenia lub utraconych zarobków, zadośćuczynienie za krzywdę (straty moralne), zwrot kosztów przygotowania do nowego zawodu (jeżeli poszkodowany stał się inwalidą), a nawet renta (m.in w wypadku, gdy poszkodowany częściowo lub całkowicie utracił zdolność do pracy zarobkowej).",
          },
          {
            q: "Jakie dowody warto zebrać w sprawie dotyczącej pogryzienia człowieka przez psa?",
            a: "Warto zebrać następujące dowody: zdjęcia obrażeń, dokumentację medyczną, ustalić świadków (należy uzyskać ich dane- imię, nazwisko, adres do doręczeń), zachować wszelką korespondencję dotyczącą sprawy. Ponadto należy ustalić dane (imię, nazwisko, adres, nr polisy) osoby chowającej lub posługującej się psem.",
          },
          {
            q: "Czy trzeba iść do sądu, czy da się załatwić polubownie sprawę dotyczącą pogryzienia człowieka przez psa?",
            a: "Niejednokrotnie da się zakończyć sprawę pozasądowo (poprzez negocjacje, mediacje, zgłoszenie szkody z polisy). Ww. sposób załatwienia sprawy jest częścią naszego pakietu dotyczącego postępowania przedsądowego. Drogę sądową inicjujemy po fiasku postępowania przedsądowego, w tym ww. sposobów rozstrzygania sporów.",
          },
          {
            q: "Ile mam czasu na dochodzenie roszczeń (przedawnienie)?",
            a: "Przedawnienie roszczeń odszkodowawczych (szkoda na osobie wyrządzona przez zwierzę) nie może skończyć się wcześniej niż z upływem lat trzech od dnia, w którym poszkodowany dowiedział się o szkodzie i o osobie obowiązanej do jej naprawienia. Przedawnienie ww. roszczeń odszkodowawczych, w przypadku osoby małoletniej, nie może skończyć się wcześniej niż z upływem lat dwóch od uzyskania przez nią pełnoletności. Jeżeli szkoda wynikła ze zbrodni lub występku, roszczenie o naprawienie szkody ulega przedawnieniu z upływem lat dwudziestu od dnia popełnienia przestępstwa bez względu na to, kiedy poszkodowany dowiedział się o szkodzie i o osobie obowiązanej do jej naprawienia (vide: art. 442[1] kc; w wypadku pogryzienia człowieka przez zwierzę może dojść do popełnienia przestępstwa, np. z art. 157 kk). Przedawnienie roszczenia o zadośćuczynienie wynosi 6 lat, a koniec tego terminu upływa w ostatnim dniu roku kalendarzowego (vide: art. 118 kc).",
          },
          {
            q: "Czy możecie Państwo reprezentować także osobę, której pies pogryzł człowieka?",
            a: "Tak, pomagamy również takim osobom, nie oceniając ich. Życie jest nieprzewidywalne, a zdarzenie szkodowe jest często wypadkową bardzo wielu zmiennych, w tym przyczynienia się pogryzionego zwierzęcia.",
          },
        ],
        ctaLabel: "Skontaktuj się z nami",
      },

      "pogryzienie-zwierzecia": {
        title: "Szkoda na zwierzęciu",
        intro: [
          "Wystarczy chwila nieuwagi na spacerze, a Twój pupil może zostać pogryziony (np. przez psa, który wybiegł przez otwartą bramę). Koszty leczenia wynikające z takiego pogryzienia mogą być znaczące, nie mówiąc już o stratach moralnych. Osobie chowającej pogryzione zwierzę przysługuje roszczenie o odszkodowanie (zwrot wszystkich wydatków związanych z pogryzieniem, w tym na leczenie, rehabilitację) oraz o zadośćuczynienie (finansowa rekompensata za straty moralne). Jak dochodzić tych roszczeń? Co zrobić, jeżeli spotkamy się z odmową ich zaspokojenia? Przychodzimy Państwu z pomocą.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "Jak pomagamy?",
        howSteps: commonHowSteps,
        pricingTitle: "Koszty pomocy",
        pricingLines: [
          "Konsultacja wstępna: od 450 zł brutto",
          "Postępowanie przedsądowe: od 900 zł brutto (w tym konsultacja wstępna), udział w mediacji dodatkowo płatny, od 300 zł brutto za sesję",
          "Postępowanie sądowe: wycena indywidualna",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: {
          title: "Odszkodowanie za pogryzione zwierzę",
          href: "https://pienkowskilegal.pl/blog/odszkodowanie-za-pogryzione-zwierze.html",
        },
        faqTitle: "FAQ",
        faq: [
          {
            q: "Kto odpowiada, gdy pies pogryzie inne zwierzę?",
            a: "Najkrócej rzecz ujmując, odpowiada osoba chowająca psa lub posługująca się nim.",
          },
          {
            q: "Czy w wypadku pogryzienia mojego psa, mogę żądać zwrotu kosztów leczenia weterynaryjnego?",
            a: "Tak, gdyż zwrot takich kosztów jest objęty roszczeniem odszkodowawczym.",
          },
          {
            q: "Czy agresywne zachowanie pogryzionego psa ma wpływ na odszkodowanie lub zadośćuczynienie?",
            a: "Tak — przebieg zdarzenia (w tym kto zaczął, czy psy były na smyczy) wpływa na zakres roszczeń o odszkdowanie lub zadośćuczynienie.",
          },
          {
            q: "Jakie dowody są najważniejsze w sprawach dotyczących pogryzienia zwierzęcia?",
            a: "Najczęściej są to rachunki i dokumentacja leczenia, zdjęcia, nagrania audio lub video, świadkowie, korespondencja między stronami.",
          },
          {
            q: "Czy da się uzyskać pieniądze z ubezpieczenia OC (np. OC w życiu prywatnym)?",
            a: "Da się, o ile druga strona sporu ma takie ubezpieczenie. Kluczowe jest prawidłowe zgłoszenie szkody podparte stosownymi dowodami oraz przekonanie ubezpieczyciela, że polisa obejmuje zdarzenie szkodowe.",
          },
          {
            q: "Czy da się zakończyć sprawę o pogryzienie zwierzęcia bez sądu?",
            a: "Da się. Pozasądowe rozstrzygnięcie sporu jest uzasadnione nie tylko ze względu na szybkość takiego rozstrzygnięcia, ale i z powodu niższych kosztów, niż przy drodze sądowej. Dlatego w ramach postępowania przedsądowego (o ile klient wyrazi na to zgodę) negocjujemy z drugą stroną lub uczestniczymy w mediacjach jako pełnomocnicy.",
          },
          {
            q: "Czy możecie Państwo prowadzić sprawę także po stronie osoby, której pies pogryzł innego psa?",
            a: "Tak, pomagamy również takim osobom, nie oceniając ich. Życie jest nieprzewidywalne, a zdarzenie szkodowe jest często wypadkową bardzo wielu zmiennych, w tym przyczynienia się pogryzionego zwierzęcia.",
          },
        ],
        ctaLabel: "Skontaktuj się z nami",
      },

      "rekojmia-zwierze": {
        title: "Rękojmia za „wadliwe” zwierzę",
        intro: [
          "Po sprzedaży zwierzęcia mogą ujawnić się jego „wady”. Zwierzę ma chorobę , nie jest rasowe lub nie ma innych właściwości, które powinno mieć, zgodnie z umową, okolicznościami lub przeznaczeniem zwierzęcia. Jak w takiej sytuacji skorzystać z uprawnień z tytułu rękojmi? Przede wszystkim kupujący może żądać obniżenia ceny takiego zwierzęcia, a w niektórych okolicznościach (jak sprzedaż zwierząt do hodowli), wymiany zwierzęcia na wolne od „wad”, usunięcia „wad” lub złożyć oświadczenie o odstąpieniu od umowy. Przychodzimy Państwu z pomocą.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "Jak pomagamy?",
        howSteps: commonHowSteps,
        pricingTitle: "Koszty pomocy",
        pricingLines: [
          "Konsultacja prawna: od 450 zł brutto",
          "Postępowanie przedsądowe: od 900 zł brutto (w tym konsultacja prawna)",
          "Postępowanie sądowe: wycena indywidualna",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: {
          title: "Co mówi prawo",
          href: "https://pienkowskilegal.pl/blog/rekojmia-za-wady-zwierzecia.html",
        },
        faqTitle: "FAQ",
        faq: [
          {
            q: "Czy na zakup psa/kota od hodowcy lub osoby prywatnej działa rękojmia?",
            a: "Ogólnie rzecz ujmując- tak, działa. Aczkolwiek strony umowy sprzedaży, mogą ograniczyć lub wyłączyć rękojmię. Przy czym, jeżeli kupującym jest konsument, ograniczenie lub wyłączenie odpowiedzialności z tytułu rękojmi jest dopuszczalne tylko w szczególnych przypadkach. Wyłączenie lub ograniczenie odpowiedzialności z tytułu rękojmi jest bezskuteczne, jeżeli sprzedawca zataił podstępnie wadę przed kupującym (dotyczy zarówno kupującego konsumenta, jak i przedsiębiorcy; vide: art. 558 kc).",
          },
          {
            q: "Jakie wady najczęściej są podstawą roszczeń?",
            a: "Najczęściej spory dotyczą następujących wad ujawnionych po sprzedaży: chorób przewlekłych, wad wrodzonych/genetycznych, problemów behawioralnych o podłożu zdrowotnym lub innych rozbieżności, pomiędzy stanem zwierzęcia wynikającym z umowy, okoliczności lub przeznaczenia zwierzęcia, a jego rzeczywistym stanem, w tym, że zwierzę nie jest rasowe.",
          },
          {
            q: "Jakie roszczenia mogę zgłosić?",
            a: "Przy rękojmi dotyczącej zwierząt przysługuje żądanie obniżenia ceny, a w niektórych przypadkach (np. sprzedaż zwierząt do hodowli), żądanie wymiany zwierzęcia na wolne od wad, żądanie usunięcia wady zwierzęcia lub odstąpienie od umowy.",
          },
          {
            q: "Jak udowodnić wadę zwierzęcia po zakupie?",
            a: "To zależy o jakiej wadzie mówimy. Jeżeli o chorobie, to najważniejsza jest dokumentacja weterynaryjna (badania, diagnoza), z której wynika wada (rękojmia przysługuje, jeżeli wada istniała już w chwili wydania zwierzęcia kupującemu), a jeśli o nierasowości zwierzęcia- badanie DNA ID zwierzęcia.",
          },
          {
            q: "Jakie są terminy na zgłoszenie roszczeń i czy liczy się czas od zakupu czy od wykrycia wady?",
            a: " Masz dwa lata, od wydania zwierzęcia na stwierdzenie wady. W ciągu roku od dnia stwierdzenia wady możesz zgłaszać roszczenia z rękojmi, a jeśli jesteś konsumentem, to termin przedawnienia, nie może się skończyć wcześniej, niż z upływem ww. dwóch lat. Jeżeli przed żądaniem obniżenia ceny albo odstąpienia od umowy bezskutecznie żądałeś wymiany zwierzęcia na wolne od wad lub usunięcia wad, bieg terminu przedawnienia na złożenie oświadczenia o odstąpieniu od umowy lub oświadczenia o obniżeniu ceny rozpoczyna się po upływie terminu wynikającego z ww. bezskutecznego żądania.",
          },
          {
            q: "Czy da się rozwiązać sprawę dotyczącą rękojmi związanej ze zwierzętami bez sądu?",
            a: "Niejednokrotnie da się zakończyć taką sprawę pozasądowo (poprzez negocjacje, mediacje). Ww. sposób załatwienia sprawy jest częścią naszego pakietu dotyczącego postępowania przedsądowego. Drogę sądową inicjujemy po fiasku postępowania przedsądowego, w tym ww. sposobów rozstrzygania sporów.",
          },
          {
            q: "Czy możecie Państwo reprezentować także sprzedawcę/hodowcę, jeśli kupujący zarzuca wady i żąda zwrotu pieniędzy?",
            a: "Tak, pomagamy również takim osobom, nie oceniając ich, o ile nie wywołuje to konfliktu interesów.",
          },
        ],
        ctaLabel: "Skontaktuj się z nami",
      },

      "spor-o-psa-po-rozstaniu": {
        title: "Spór o zwierzę po rozstaniu",
        intro: [
          "Ludzką rzeczą jest się rozstać, nie zawsze jednak odbywa się to „po ludzku”. Co zrobić, kiedy w związku dwojga ludzi było zwierzę? Żeby rozstrzygnąć „czyje ono jest”, trzeba poznać okoliczności dotyczące ww. związku (formalny, ze wspólnością majątkową, z rozdzielnością majątkową, nieformalny), jak i okoliczności dotyczące nabycia zwierzęcia (z umowy sprzedaży, ze schroniska, znalezione). Przede wszystkim,  należy pamiętać o jednym- zwierzę to nie jest rzecz, a przepisy dotyczące rzeczy stosuje się do zwierzęcia tylko „odpowiednio”. Przychodzimy Państwu z pomocą.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "Jak pomagamy?",
        howSteps: commonHowSteps,
        pricingTitle: "Koszty pomocy",
        pricingLines: [
          "Konsultacja wstępna: od 600 zł",
          "Postępowanie przedsądowe: od 1200 zł (w tym konsultacja wstępna)",
          "Sprawa sądowa: wycena indywidualna",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: { title: "", href: "" },
        faqTitle: "FAQ",
        faq: [
          {
            q: "Do kogo należy pies po rozstaniu?",
            a: "To zależy od wielu czynników. Wbrew pozorom, najbardziej istotne nie musi być dla sądu to, co jest na papierze. Czyli kto figuruje jako kupujący w umowie sprzedaży, czyje dane są w książeczce zdrowia, itp. Oczywiście umowa sprzedaży lub ksiażeczka zdrowia są dowodami w sprawie, a w przypadku związku małżeńskiego ustala się, czy zwierzę wchodzi w skład majątku wspólnego. Jednak kluczowe powinno być to, kto faktycznie sprawował lub sprawuje opiekę nad zwierzęciem, kto ponosił lub ponosi koszty utrzymania zwierzęcia, z kim zwierzę jest bardziej związane. Dokonanie takiego ustalenia nie musi należeć do najprostszych.",
          },
          {
            q: "Czy mogę żądać wydania psa i jak wygląda taka sprawa formalnie?",
            a: "Możesz wytoczyć powództwo windykacyjne, a wcześniej wezwać partnera do wydnia psa, gdyż w stosunku do zwierząt stosuje się odpowiednio przepisy dotyczące rzeczy. Tym niemniej jednak sąd może oddalić powództwo, jeśli ustali, że Twój partner ma tytuł prawny do zwierzęcia lub jest z nim faktycznie związany, bardziej niż Ty. Takie sprawy są trudne dowodowo. Dlatego zachęcamy do rozważenia pozasądowych sposobów rozstrzygania sporów, które są częścią naszego pakietu przedsądowego.",
          },
          {
            q: "Czy da się ustalić zasady opieki (np. naprzemienna opieka, kontakty), zamiast sporu „kto ma psa”?",
            a: "Tak, w drodze umowy zawartej pomiędzy partnerami lub w drodze ugody zawartej przed mediatorem. Natomiast Sąd powinien przyznać psa jednej ze stron.",
          },
          {
            q: "Jakie dowody są najważniejsze w sporze o psa po rozstaniu?",
            a: "Najczęściej jest to umowa sprzedaży, umowa adopcyjna, potwierdzenia płatności tytułem kosztów utrzymania psa, dokumentacja weterynaryjna, dane z rejestru chip, korespondencja między stronami, zeznania świadków, przesłuchanie stron.",
          },
          {
            q: "Czy da się to załatwić bez sądu?",
            a: "Bardzo często tak — dobrze poprowadzone negocjacje lub mediacje oraz sensowna umowa lub ugoda potrafią zakończyć temat.",
          },
        ],
        ctaLabel: "Skontaktuj się z nami",
      },

      "blad-weterynaryjny": {
        title: "Błąd weterynaryjny",
        intro: [
          "Lekarz weterynarii może postawić niewłaściwą diagnozę, zalecić niewłaściwą terapię,  dobrać złą technikę zabiegu weterynaryjnego. Błąd lekarza weterynarii może wynikać ze złej organizacji kliniki. Niezależnie od źródła błędu, jego ofiarą jest zwierzę. „Właściciel” może żądać zapłaty odszkodowania (w tym kosztów leczenia, kosztów rehabilitacji zwierzęcia albo zwrotu wartości zwierzęcia, w razie jego śmierci w wyniku błędu weterynaryjnego) oraz zadośćuczynienia za doznaną krzywdę (za straty moralne). Lekarz weterynarii może być również postawiony przed rzecznikiem dyscyplinarnym, a w skrajnym przypadkach odpowiadać w postępowaniu karnym. Przychodzimy Państwu z pomocą.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "Jak pomagamy?",
        howSteps: commonHowSteps,
        pricingTitle: "Koszty pomocy",
        pricingLines: [
          "Konsultacja wstępna: od 450 zł",
          "Postępowanie przedsądowe: od 1000 zł",
          "Postępowanie sądowe: wycena indywidualna",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: { title: "C", href: "" },
        faqTitle: "FAQ",
        faq: [
          {
            q: "Czym różni się błąd weterynaryjny od powikłań pozabiegowych?",
            a: "Nie każdy niekorzystny skutek leczenia jest wynikiem błędu. Należy ocenić całokształt sytuacji, stan zdrowia zwierzęcia przed i po zabiegu, działania lub zaniechania lekarzy, organizację kliniki weterynaryjnej.",
          },
          {
            q: "Jakie roszczenia mogą wchodzić w grę przy błędzie weterynarza?",
            a: "Chodzi o odszkodowanie, czyli zwrot kosztów leczenia, rehabilitacji, a w przypadku śmierci zwierzęcia- jego wartości. Ponadto przysługuje rekompensata finansowa (zadośćuczynienie) za straty moralne związane z błędem weterynaryjnym.",
          },
          {
            q: "Jakie dowody są kluczowe w sprawie o błąd weterynaryjny?",
            a: "Historia leczenia zwierzęcia, w tym: dokumenty diagnostyczne, terapueutyczne, zalecenia, zgody, faktury/rachunki, zeznania świadków, dowód z opinii biełego, przesłuchanie stron.",
          },
          {
            q: "Czy potrzebna jest opinia biegłego albo druga opinia weterynaryjna?",
            a: "Tak, jeżeli strony nie będą w stanie pozasądowo rozwiązać sporu, do czego zachęcamy w ramach naszego pakietu przedsądowego.",
          },
          {
            q: "Czy mogę zgłosić sprawę do ubezpieczyciela OC kliniki lub lekarza weterynarii?",
            a: "Tak i my możemy w tym pomóc. Trzeba opisać zdarzenie szkodowe i przekonać ubezpieczyciela, że ubezpieczenie obejmuje to zdarzenie.",
          },
          {
            q: "Czy da się to zakończyć polubownie bez sądu?",
            a: "Często tak — pomagamy w negocjacjach lub mediacjach, w ramach naszego pakietu przedsądowego, gdyż to najszybsza i najbardziej zasadna ekonomicznie droga do rostrzygnięcia sporu o błąd weterynaryjny.",
          },
          {
            q: "Czy możecie reprezentować weterynarza/klinikę, jeśli klient zarzuca błąd weterynaryjny?",
            a: "Tak — możemy, o ile nie wywołuje to konfliktu interesów. Nikogo nie oceniamy, każda sytuacja ma swoją indywidualną specyfikę, na któą składa się wiele wypadkowych.",
          },
        ],
        ctaLabel: "Skontaktuj się z nami",
      },
    };

    // ====== EN/ES 1:1 (pełne tłumaczenie, bez PL odpadów) ======
    // Uwaga: zachowujemy identyczną strukturę jak PL, tylko teksty tłumaczymy.

    const baseEN: Record<LandingKey, LandingCopy> = {
      "pogryzienie-czlowieka": {
        title: "Personal injury caused by an animal",
        intro: [
          "The legal consequences of an animal attack on a person are complex. It is not only about compensation for physical injury. A person attacked by an animal may also claim non-pecuniary damages for moral harm, for example fear of animals. However, the scope of potential claims does not end there. A victim who became disabled may demand a sum necessary to retrain for a new profession. In extreme cases, such as partial or total loss of earning capacity, the victim may claim an annuity. We are here to help you.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "How do we help?",
        howSteps: commonHowSteps,
        pricingTitle: "Costs of assistance",
        pricingLines: [
          "Initial consultation: from PLN 500 gross",
          "Pre-litigation stage: from PLN 1,000 gross (initial consultation included), participation in mediation additionally paid, from PLN 300 gross per session",
          "Court proceedings: individual pricing",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: { title: "", href: "" },
        faqTitle: "FAQ",
        faq: [
          {
            q: "Who is liable when a dog bites a person, and is it always the owner?",
            a: "Liability rests with the person who keeps the animal or uses it; it is not always the owner (see: Article 431 of the Polish Civil Code).",
          },
          {
            q: "Does the behaviour of the dog or the injured person affect the scope of liability?",
            a: "Yes — the course of events (e.g. entering a property without permission) affects liability and the scope of claims. Each case is assessed based on evidence.",
          },
          {
            q: "What claims can I pursue after being bitten by a dog?",
            a: "Possible claims include reimbursement of treatment costs or lost earnings, compensation for pain and suffering (moral harm), reimbursement of retraining costs (if the victim became disabled) and even an annuity (e.g. if the victim partially or totally lost earning capacity).",
          },
          {
            q: "What evidence is worth collecting in a case of a dog bite injury?",
            a: "It is worth collecting: photos of injuries, medical documentation, identifying witnesses (name, surname, service address), keeping all correspondence. You should also establish the details (name, address, policy number) of the person who keeps/uses the dog.",
          },
          {
            q: "Do I have to go to court, or can a dog bite case be resolved amicably?",
            a: "Often it can be resolved out of court (negotiations, mediation, insurance claim). This is part of our pre-litigation package. We initiate court proceedings after the pre-litigation stage fails, including the above dispute-resolution methods.",
          },
          {
            q: "How much time do I have to pursue claims (limitation period)?",
            a: "Limitation rules depend on the legal basis. In personal injury cases, the limitation period cannot end earlier than 3 years from the date the injured person learned about the damage and the liable person. For minors, it cannot end earlier than 2 years after reaching adulthood. If the damage resulted from a felony or misdemeanour, the claim becomes time-barred after 20 years from the offence regardless of knowledge (see: Art. 442[1] CC; an animal attack may involve a criminal offence, e.g. Art. 157 of the Penal Code). The limitation period for non-pecuniary damages is 6 years and ends on the last day of the calendar year (see: Art. 118 CC).",
          },
          {
            q: "Can you also represent the person whose dog bit someone?",
            a: "Yes, we also help such people without judging them. Life is unpredictable, and incidents are often the result of many factors, including contributory behaviour of the injured party.",
          },
        ],
        ctaLabel: "Contact us",
      },

      "pogryzienie-zwierzecia": {
        title: "Injury to an animal",
        intro: [
          "A moment of inattention during a walk may be enough for your pet to be bitten (e.g. by a dog that ran out through an open gate). Treatment costs can be significant, not to mention moral harm. The caretaker of the bitten animal has a claim for damages (reimbursement of all bite-related expenses, including treatment and rehabilitation) and non-pecuniary damages (financial compensation for moral harm). How to pursue these claims? What if we face a refusal to satisfy them? We are here to help you.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "How do we help?",
        howSteps: commonHowSteps,
        pricingTitle: "Costs of assistance",
        pricingLines: [
          "Initial consultation: from PLN 450 gross",
          "Pre-litigation stage: from PLN 900 gross (including the initial consultation), participation in mediation additionally paid, from PLN 300 gross per session",
          "Court proceedings: individual pricing",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: {
          title: "Compensation for a bitten animal",
          href: "https://pienkowskilegal.pl/blog/odszkodowanie-za-pogryzione-zwierze.html",
        },
        faqTitle: "FAQ",
        faq: [
          { q: "Who is liable when a dog bites another animal?", a: "In short: the person who keeps the dog or uses it is liable." },
          { q: "If my dog is bitten, can I claim reimbursement of veterinary treatment costs?", a: "Yes, the reimbursement of such costs is covered by a damages claim." },
          { q: "Does the bitten dog’s aggressive behaviour affect damages or non-pecuniary damages?", a: "Yes — the course of events (including who started, whether dogs were on a leash) affects the scope of claims." },
          { q: "What evidence is most important in animal bite cases?", a: "Most often: invoices and treatment documentation, photos, audio/video recordings, witnesses and correspondence between the parties." },
          { q: "Can I obtain money from liability insurance (e.g. private OC)?", a: "Yes, if the other side has such insurance. The key is proper claim notification supported by evidence and persuading the insurer that the policy covers the incident." },
          { q: "Can an animal bite case be concluded without court?", a: "Yes. Out-of-court resolution is justified due to speed and lower costs. Therefore, within the pre-litigation stage (if the client agrees) we negotiate with the other side or participate in mediation as attorneys." },
          { q: "Can you also represent the person whose dog bit another dog?", a: "Yes, we also help such people without judging them. Life is unpredictable and incidents are often the result of many factors, including contributory behaviour of the injured animal/caretaker." },
        ],
        ctaLabel: "Contact us",
      },

      "rekojmia-zwierze": {
        title: 'Warranty for a “defective” animal',
        intro: [
          "After the sale of an animal, its “defects” may be revealed. The animal may be ill, not purebred or lack other features it should have according to the agreement, circumstances or intended purpose. How can you use warranty rights in such a situation? First of all, the buyer may demand a price reduction, and in some circumstances (such as sale of animals for breeding), replacement with an animal free of “defects”, removal of “defects” or submit a statement of withdrawal from the contract. We are here to help you.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "How do we help?",
        howSteps: commonHowSteps,
        pricingTitle: "Costs of assistance",
        pricingLines: [
          "Legal consultation: from PLN 450 gross",
          "Pre-litigation stage: from PLN 900 gross (including legal consultation)",
          "Court proceedings: individual pricing",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: {
          title: "What the law says",
          href: "https://pienkowskilegal.pl/blog/rekojmia-za-wady-zwierzecia.html",
        },
        faqTitle: "FAQ",
        faq: [
          {
            q: "Does warranty apply when buying a dog/cat from a breeder or a private person?",
            a: "Generally yes, it applies. However, the parties may limit or exclude warranty. If the buyer is a consumer, limitation/exclusion is allowed only in specific cases. Exclusion/limitation is ineffective if the seller fraudulently concealed the defect (applies to consumers and businesses; see Art. 558 CC).",
          },
          {
            q: "Which defects most often form the basis of claims?",
            a: "Most often disputes concern: chronic diseases, congenital/genetic defects, behavioural problems with a health basis or other discrepancies between the animal’s state as per agreement/circumstances/purpose and its actual state, including that the animal is not purebred.",
          },
          {
            q: "What claims can I make?",
            a: "In animal warranty cases you may demand a price reduction and, in some cases (e.g. sale for breeding), replacement with an animal free of defects, removal of the defect or withdrawal from the contract.",
          },
          {
            q: "How can I prove a defect after purchase?",
            a: "It depends on the defect. If it is an illness, the key is veterinary documentation (tests, diagnosis) showing the defect (warranty applies if the defect existed at the time of delivery). If it is lack of purebred status — a DNA ID test of the animal.",
          },
          {
            q: "What are the deadlines, and do they run from purchase or discovery?",
            a: "You have two years from delivery of the animal to detect a defect. Within one year from discovering the defect you may pursue warranty claims, and if you are a consumer, the limitation period cannot end earlier than those two years. If before seeking a price reduction/withdrawal you unsuccessfully demanded replacement or removal of defects, the limitation period for submitting the statement of withdrawal or price reduction starts after the deadline from that unsuccessful demand.",
          },
          {
            q: "Can an animal warranty case be resolved without court?",
            a: "Often yes (negotiations, mediation). This is part of our pre-litigation package. We initiate court proceedings after the pre-litigation stage fails, including the above dispute-resolution methods.",
          },
          {
            q: "Can you also represent the seller/breeder if the buyer alleges defects and demands a refund?",
            a: "Yes, we also help such people without judging them, provided this does not create a conflict of interest.",
          },
        ],
        ctaLabel: "Contact us",
      },

      "spor-o-psa-po-rozstaniu": {
        title: "Dispute over an animal after a breakup",
        intro: [
          "Breaking up is human, but it does not always happen “humanely”. What if there was an animal in the relationship? To determine “whose it is”, one must understand the circumstances of the relationship (formal marriage with community property, separation of property, informal relationship) and the circumstances of acquiring the animal (sale contract, shelter adoption, found). Most importantly, remember one thing: an animal is not a thing, and provisions on things apply to animals only “appropriately”. We are here to help you.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "How do we help?",
        howSteps: commonHowSteps,
        pricingTitle: "Costs of assistance",
        pricingLines: [
          "Initial consultation: from PLN 600",
          "Pre-litigation stage: from PLN 1,200 (including the initial consultation)",
          "Court case: individual pricing",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: { title: "", href: "" },
        faqTitle: "FAQ",
        faq: [
          {
            q: "Who does the dog belong to after a breakup?",
            a: "It depends on many factors. Contrary to appearances, the most important thing for the court may not be what is “on paper” (who is listed as the buyer, whose details are in the health booklet, etc.). These are evidence, and in marriage the court also determines whether the animal is part of joint property. However, key should be who actually cared for the animal, who covered the costs and with whom the animal is more bonded. Determining this may not be easy.",
          },
          {
            q: "Can I demand the dog’s return and what does such a case look like formally?",
            a: "You may bring a vindicatory claim and earlier demand return, since rules on things apply appropriately to animals. However, the court may dismiss the claim if it finds that your partner has legal title to the animal or is more closely connected to it than you. These cases are evidentially difficult, so we encourage considering out-of-court methods included in our pre-litigation package.",
          },
          {
            q: "Can care rules be agreed (alternating care, contact) instead of a dispute over who has the dog?",
            a: "Yes, by an agreement between partners or a settlement before a mediator. However, the court should award the dog to one of the parties.",
          },
          {
            q: "What evidence is most important in a dispute over a dog after a breakup?",
            a: "Most often: sale contract, adoption contract, proof of payments for upkeep, veterinary documentation, chip registry data, correspondence, witness testimony and hearing of the parties.",
          },
          {
            q: "Can it be resolved without court?",
            a: "Very often yes — well-conducted negotiations or mediation and a sensible agreement/settlement can end the matter.",
          },
        ],
        ctaLabel: "Contact us",
      },

      "blad-weterynaryjny": {
        title: "Veterinary malpractice",
        intro: [
          "A veterinarian may make an incorrect diagnosis, recommend inappropriate therapy or choose a wrong surgical technique. An error may also result from poor clinic organisation. Regardless of the source, the victim is the animal. The “owner” may claim damages (including treatment and rehabilitation costs or the animal’s value if it dies as a result of malpractice) and non-pecuniary damages for moral harm. The veterinarian may also face disciplinary proceedings and, in extreme cases, criminal liability. We are here to help you.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "How do we help?",
        howSteps: commonHowSteps,
        pricingTitle: "Costs of assistance",
        pricingLines: [
          "Initial consultation: from PLN 450",
          "Pre-litigation stage: from PLN 1,000",
          "Court proceedings: individual pricing",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: { title: "C", href: "" },
        faqTitle: "FAQ",
        faq: [
          {
            q: "How does veterinary malpractice differ from post-operative complications?",
            a: "Not every adverse outcome is a result of malpractice. The whole situation must be assessed: the animal’s health before and after, actions/omissions of doctors and the clinic’s organisation.",
          },
          {
            q: "What claims may arise in case of veterinary malpractice?",
            a: "Damages: reimbursement of treatment and rehabilitation costs and, if the animal dies, its value. In addition, non-pecuniary damages for moral harm related to malpractice may be due.",
          },
          {
            q: "What evidence is key in a malpractice case?",
            a: "Treatment history, including diagnostic and therapeutic documents, recommendations, consents, invoices/receipts, witness testimony, expert opinion evidence and hearing of the parties.",
          },
          {
            q: "Is an expert opinion or second veterinary opinion needed?",
            a: "Yes, if the parties cannot resolve the dispute out of court, which we encourage under our pre-litigation package.",
          },
          {
            q: "Can I report the case to the clinic’s/vet’s liability insurer?",
            a: "Yes, and we can help. You need to describe the incident and convince the insurer that the insurance covers it.",
          },
          {
            q: "Can it be resolved amicably without court?",
            a: "Often yes — we help in negotiations or mediation under our pre-litigation package, as it is the fastest and most economically justified path to resolve a malpractice dispute.",
          },
          {
            q: "Can you represent a vet/clinic if a client alleges malpractice?",
            a: "Yes — provided this does not create a conflict of interest. We do not judge anyone; each situation has its individual specifics shaped by many factors.",
          },
        ],
        ctaLabel: "Contact us",
      },
    };

    const baseES: Record<LandingKey, LandingCopy> = {
      "pogryzienie-czlowieka": {
        title: "Daño personal causado por un animal",
        intro: [
          "Las consecuencias jurídicas del ataque de un animal a una persona son complejas. No se trata solo de indemnización por el daño físico. La persona atacada también puede reclamar compensación por daños morales, por ejemplo el miedo a los animales. Sin embargo, el alcance de las posibles reclamaciones no termina ahí. La víctima que se convirtió en inválida puede exigir el pago de la suma necesaria para prepararse para una nueva profesión. En casos extremos, como la pérdida parcial o total de la capacidad de trabajar, la víctima puede reclamar una renta. Estamos para ayudarles.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "¿Cómo ayudamos?",
        howSteps: commonHowSteps,
        pricingTitle: "Costes de la ayuda",
        pricingLines: [
          "Consulta inicial: desde 500 PLN brutos",
          "Fase extrajudicial: desde 1.000 PLN brutos (consulta inicial incluida), mediación con coste adicional, desde 300 PLN brutos por sesión",
          "Procedimiento judicial: precio individual",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: { title: "", href: "" },
        faqTitle: "FAQ",
        faq: [
          {
            q: "¿Quién responde si un perro muerde a una persona y siempre es el dueño?",
            a: "Responde la persona que custodia el animal o lo utiliza; no siempre es el dueño (véase el art. 431 del Código Civil polaco).",
          },
          {
            q: "¿El comportamiento del perro o de la víctima influye en el alcance de la responsabilidad?",
            a: "Sí — el desarrollo del suceso (p. ej., entrar en una propiedad sin permiso) afecta la responsabilidad y el alcance de las reclamaciones. Cada caso se valora según las pruebas.",
          },
          {
            q: "¿Qué reclamaciones puedo plantear tras una mordedura de perro?",
            a: "Entre otras: reembolso de gastos de tratamiento o salarios perdidos, compensación por daño moral, reembolso de costes de reconversión profesional (si la víctima quedó inválida) e incluso una renta (por ejemplo si perdió parcial o totalmente la capacidad de trabajar).",
          },
          {
            q: "¿Qué pruebas conviene reunir en un caso de mordedura?",
            a: "Conviene reunir: fotos de lesiones, documentación médica, identificar testigos (nombre, apellidos, dirección para notificaciones), conservar toda correspondencia. Además, determinar datos (nombre, dirección, nº de póliza) de la persona que custodia/usa el perro.",
          },
          {
            q: "¿Hay que ir a juicio o puede resolverse amistosamente?",
            a: "A menudo se puede resolver fuera de los tribunales (negociación, mediación, reclamación al seguro). Esto forma parte de nuestro paquete extrajudicial. Iniciamos la vía judicial tras el fracaso de la fase extrajudicial, incluidas esas vías.",
          },
          {
            q: "¿Cuánto tiempo tengo para reclamar (prescripción)?",
            a: "Las reglas dependen de la base legal. En daños personales, la prescripción no puede terminar antes de 3 años desde que la víctima supo del daño y del responsable. Para menores, no puede terminar antes de 2 años tras la mayoría de edad. Si el daño provino de un delito, prescribe a los 20 años desde el delito (art. 442[1] CC). La acción de daño moral prescribe a los 6 años y el plazo vence el último día del año natural (art. 118 CC).",
          },
          {
            q: "¿Podéis representar también a la persona cuyo perro mordió a alguien?",
            a: "Sí, también ayudamos en esos casos sin juzgar. La vida es imprevisible y los incidentes suelen ser resultado de muchos factores, incluida la contribución de la parte lesionada.",
          },
        ],
        ctaLabel: "Contacta con nosotros",
      },

      "pogryzienie-zwierzecia": {
        title: "Daño a un animal",
        intro: [
          "Basta un momento de descuido durante el paseo para que tu mascota sea mordida (p. ej., por un perro que salió por una puerta abierta). Los costes de tratamiento pueden ser significativos, sin mencionar los daños morales. A la persona que cuida del animal mordido le corresponde una reclamación de daños (reembolso de todos los gastos, incluido tratamiento y rehabilitación) y una compensación por daño moral. ¿Cómo reclamar? ¿Qué hacer si se niegan? Estamos para ayudarles.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "¿Cómo ayudamos?",
        howSteps: commonHowSteps,
        pricingTitle: "Costes de la ayuda",
        pricingLines: [
          "Consulta inicial: desde 450 PLN brutos",
          "Fase extrajudicial: desde 900 PLN brutos (incluye consulta inicial), mediación con coste adicional, desde 300 PLN brutos por sesión",
          "Procedimiento judicial: precio individual",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: {
          title: "Indemnización por animal mordido",
          href: "https://pienkowskilegal.pl/blog/odszkodowanie-za-pogryzione-zwierze.html",
        },
        faqTitle: "FAQ",
        faq: [
          { q: "¿Quién responde si un perro muerde a otro animal?", a: "En resumen: responde quien custodia el perro o lo utiliza." },
          { q: "Si mi perro fue mordido, ¿puedo reclamar los gastos veterinarios?", a: "Sí, el reembolso de esos gastos forma parte de la indemnización por daños." },
          { q: "¿El comportamiento agresivo del perro mordido afecta la indemnización o el daño moral?", a: "Sí — el desarrollo del suceso (quién empezó, si iban con correa) afecta el alcance de las reclamaciones." },
          { q: "¿Qué pruebas son más importantes?", a: "Normalmente: facturas y documentación de tratamiento, fotos, grabaciones de audio/vídeo, testigos y correspondencia entre las partes." },
          { q: "¿Se puede obtener dinero del seguro de responsabilidad (OC)?", a: "Sí, si la otra parte tiene ese seguro. Lo clave es notificar correctamente con pruebas y convencer a la aseguradora de que la póliza cubre el incidente." },
          { q: "¿Se puede cerrar sin juicio?", a: "Sí. La vía extrajudicial es más rápida y barata. Por eso, en la fase extrajudicial (si el cliente lo acepta) negociamos o participamos en mediación como representantes." },
          { q: "¿Podéis representar también a la persona cuyo perro mordió a otro perro?", a: "Sí, también ayudamos sin juzgar. Los incidentes suelen ser resultado de muchos factores, incluida la contribución del animal/custodio lesionado." },
        ],
        ctaLabel: "Contacta con nosotros",
      },

      "rekojmia-zwierze": {
        title: 'Garantía por animal “defectuoso”',
        intro: [
          "Tras la venta del animal pueden revelarse “defectos”. El animal tiene una enfermedad, no es de raza o carece de características que debería tener según el contrato, las circunstancias o el destino. ¿Cómo usar los derechos de garantía? El comprador puede exigir rebaja del precio y, en algunas circunstancias (p. ej., venta para cría), sustitución por un animal sin “defectos”, eliminación de “defectos” o declarar la resolución del contrato. Estamos para ayudarles.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "¿Cómo ayudamos?",
        howSteps: commonHowSteps,
        pricingTitle: "Costes de la ayuda",
        pricingLines: [
          "Consulta jurídica: desde 450 PLN brutos",
          "Fase extrajudicial: desde 900 PLN brutos (incluye consulta jurídica)",
          "Procedimiento judicial: precio individual",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: {
          title: "Qué dice la ley",
          href: "https://pienkowskilegal.pl/blog/rekojmia-za-wady-zwierzecia.html",
        },
        faqTitle: "FAQ",
        faq: [
          {
            q: "¿Aplica la garantía al comprar un perro/gato a un criador o particular?",
            a: "En general sí. Sin embargo, las partes pueden limitar o excluir la garantía. Si el comprador es consumidor, la limitación/exclusión solo es posible en casos especiales. Es ineficaz si el vendedor ocultó fraudulentamente el defecto (art. 558 CC).",
          },
          {
            q: "¿Qué defectos suelen ser la base de reclamaciones?",
            a: "Normalmente: enfermedades crónicas, defectos congénitos/genéticos, problemas conductuales de base médica u otras discrepancias entre el estado esperado y el real, incluido que el animal no sea de raza.",
          },
          {
            q: "¿Qué reclamaciones puedo presentar?",
            a: "Se puede pedir rebaja del precio y, en algunos casos (p. ej., cría), sustitución por un animal sin defectos, eliminación del defecto o resolución del contrato.",
          },
          {
            q: "¿Cómo probar el defecto tras la compra?",
            a: "Depende. Si es enfermedad: documentación veterinaria que demuestre que el defecto existía al entregarse. Si es no ser de raza: prueba de ADN/ID del animal.",
          },
          {
            q: "¿Qué plazos aplican?",
            a: "Tienes dos años desde la entrega para constatar el defecto. En un año desde descubrirlo puedes reclamar por garantía y, si eres consumidor, la prescripción no puede acabar antes de esos dos años. Si antes pediste sin éxito sustitución o eliminación, el plazo para declarar resolución o rebaja empieza tras vencer el plazo de esa petición fallida.",
          },
          {
            q: "¿Se puede resolver sin juicio?",
            a: "A menudo sí (negociación, mediación). Esto forma parte del paquete extrajudicial. Vamos a juicio tras el fracaso de la fase extrajudicial, incluidas esas vías.",
          },
          {
            q: "¿Podéis representar también al vendedor/criador?",
            a: "Sí, siempre que no genere conflicto de intereses.",
          },
        ],
        ctaLabel: "Contacta con nosotros",
      },

      "spor-o-psa-po-rozstaniu": {
        title: "Disputa por un animal tras la ruptura",
        intro: [
          "Es humano separarse, pero no siempre ocurre “de forma humana”. ¿Qué hacer cuando en la relación había un animal? Para decidir “de quién es”, hay que conocer las circunstancias de la relación (formal con comunidad, separación, informal) y la forma de adquisición (compraventa, refugio, encontrado). Sobre todo, hay que recordar una cosa: el animal no es una cosa, y las normas sobre cosas se aplican solo “de manera adecuada”. Estamos para ayudarles.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "¿Cómo ayudamos?",
        howSteps: commonHowSteps,
        pricingTitle: "Costes de la ayuda",
        pricingLines: [
          "Consulta inicial: desde 600 PLN",
          "Fase extrajudicial: desde 1.200 PLN (incluye consulta inicial)",
          "Asunto judicial: precio individual",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: { title: "", href: "" },
        faqTitle: "FAQ",
        faq: [
          {
            q: "¿A quién pertenece el perro tras la ruptura?",
            a: "Depende de muchos factores. Puede que lo más importante para el tribunal no sea lo que está “en papel”. Son pruebas, y en matrimonio se analiza si el animal entra en el patrimonio común. Pero lo clave suele ser quién cuidaba realmente del animal, quién pagaba los costes y con quién está más vinculado. Determinarlo puede no ser sencillo.",
          },
          {
            q: "¿Puedo exigir la devolución del perro y cómo es el procedimiento?",
            a: "Puedes interponer una acción reivindicatoria y antes requerir la devolución. Sin embargo, el tribunal puede desestimar si tu pareja tiene título jurídico o está más vinculada al animal que tú. Son asuntos difíciles en prueba, por eso recomendamos vías extrajudiciales incluidas en el paquete extrajudicial.",
          },
          {
            q: "¿Se pueden acordar reglas de cuidado en lugar de discutir quién se queda el perro?",
            a: "Sí, mediante contrato entre las partes o acuerdo ante mediador. No obstante, el tribunal debería atribuir el perro a una de las partes.",
          },
          {
            q: "¿Qué pruebas son las más importantes?",
            a: "Normalmente: contrato de compra, contrato de adopción, comprobantes de gastos, documentación veterinaria, registro de chip, correspondencia, testigos y declaración de las partes.",
          },
          {
            q: "¿Se puede resolver sin juicio?",
            a: "Muy a menudo sí — negociación o mediación bien llevadas y un acuerdo razonable pueden cerrar el tema.",
          },
        ],
        ctaLabel: "Contacta con nosotros",
      },

      "blad-weterynaryjny": {
        title: "Mala praxis veterinaria",
        intro: [
          "El veterinario puede hacer un diagnóstico incorrecto, recomendar terapia inadecuada o elegir una técnica quirúrgica incorrecta. El error también puede resultar de mala organización de la clínica. Sea cual sea la causa, la víctima es el animal. El “propietario” puede reclamar indemnización (incluidos costes de tratamiento, rehabilitación o el valor del animal si muere por mala praxis) y compensación por daño moral. El veterinario también puede afrontar un procedimiento disciplinario y, en casos extremos, responsabilidad penal. Estamos para ayudarles.",
          "",
        ],
        situationsTitle: "",
        situations: ["", "", "", ""],
        howTitle: "¿Cómo ayudamos?",
        howSteps: commonHowSteps,
        pricingTitle: "Costes de la ayuda",
        pricingLines: [
          "Consulta inicial: desde 450 PLN",
          "Fase extrajudicial: desde 1.000 PLN",
          "Procedimiento judicial: precio individual",
        ],
        sidesNoteTitle: "",
        sidesNote: "",
        related: { title: "C", href: "" },
        faqTitle: "FAQ",
        faq: [
          {
            q: "¿En qué se diferencia la mala praxis de las complicaciones posoperatorias?",
            a: "No todo resultado desfavorable se debe a un error. Hay que valorar la situación completa: salud antes y después, acciones u omisiones de los médicos y organización de la clínica.",
          },
          {
            q: "¿Qué reclamaciones pueden proceder?",
            a: "Indemnización: reembolso de tratamiento, rehabilitación y, si el animal muere, su valor. Además, compensación económica por daño moral relacionado con la mala praxis.",
          },
          {
            q: "¿Qué pruebas son clave?",
            a: "Historia clínica: documentos diagnósticos y terapéuticos, recomendaciones, consentimientos, facturas/recibos, testigos, peritaje y declaración de las partes.",
          },
          {
            q: "¿Se necesita peritaje u otra opinión veterinaria?",
            a: "Sí, si las partes no pueden resolver extrajudicialmente, lo que fomentamos dentro del paquete extrajudicial.",
          },
          {
            q: "¿Puedo comunicarlo a la aseguradora de responsabilidad de la clínica/veterinario?",
            a: "Sí, y podemos ayudar. Hay que describir el hecho y convencer a la aseguradora de que el seguro cubre el incidente.",
          },
          {
            q: "¿Se puede resolver amistosamente sin juicio?",
            a: "A menudo sí — ayudamos en negociación o mediación como parte del paquete extrajudicial, por ser la vía más rápida y económicamente razonable.",
          },
          {
            q: "¿Podéis representar al veterinario/la clínica si el cliente acusa mala praxis?",
            a: "Sí — si no genera conflicto de intereses. No juzgamos; cada caso tiene su especificidad y múltiples factores.",
          },
        ],
        ctaLabel: "Contacta con nosotros",
      },
    };

    const base = locale === "en" ? baseEN : locale === "es" ? baseES : basePL;

    if (isLandingKey(slug, base)) return base[slug];
    return base["pogryzienie-czlowieka"];
  }, [slug, locale]);

  useEffect(() => {
    const baseTitle =
      locale === "pl"
        ? "Pieńkowski – Kancelaria Adwokacka"
        : locale === "en"
        ? "Pieńkowski – Law Office"
        : "Pieńkowski – Despacho de Abogados";

    document.title = `${copy.title} | ${baseTitle}`;
  }, [copy.title, locale]);

  const blogPrefix = locale === "en" ? "Read on the blog: " : locale === "es" ? "Lee en el blog: " : "Czytaj na blogu: ";
  const optionLabel = locale === "en" ? "Option" : locale === "es" ? "Opción" : "Opcja";

  return (
    <main className="zwc-landing">
      <section>
        <div className="container">
          <h1 className="landing-title">{copy.title}</h1>

          <p className="landing-lead">{copy.intro?.[0] ?? ""}</p>

          <div className="section-intro">
            {(copy.intro ?? [])
              .slice(1)
              .filter((p) => Boolean(p && p.trim().length)) // ✅ usuwa puste akapity (likwiduje “dziwny odstęp”)
              .map((p) => (
                <p key={p}>{p}</p>
              ))}
          </div>

         {copy.related?.href && copy.related.href.trim().length > 0 ? (
  <div style={{ marginTop: 10 }}>
    <a href={copy.related.href} className="inline-link">
      {blogPrefix}
      {copy.related.title}
    </a>
  </div>
) : (
  <div style={{ marginTop: 10 }}>
    <a href="/blog#paragraf-zwierzoochronny" className="inline-link">
      {locale === 'en'
        ? 'Visit our blog'
        : locale === 'es'
        ? 'Visita nuestro blog'
        : 'Zajrzyj na nasz blog'}
    </a>
  </div>
)}

          {/* ✅ USUNIĘTE: situations (to było źródłem kropek i dużego odstępu) */}

          <h2 style={{ marginTop: 34 }}>{copy.howTitle}</h2>

          <div className="forwhom-grid forwhom-grid--onecol" style={{ marginTop: 14 }}>
            {(copy.howSteps ?? []).map((step, index) => (
              <div
                key={step.title ?? index}
                style={{
                  display: "flex",
                  gap: 16,
                  alignItems: "flex-start",
                  padding: 0,
                  border: "none",
                  background: "transparent",
                }}
              >
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 999,
                    background: "#FBF8F3",
                    border: "2px solid #0C3831",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    color: "#111",
                    flex: "0 0 38px",
                    lineHeight: "38px",
                  }}
                  aria-hidden="true"
                >
                  {index + 1}
                </div>

                <div>
                  <h3 style={{ marginTop: 0, marginBottom: 6 }}>{step.title}</h3>
                  <p className="muted" style={{ margin: 0 }}>
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <h2 style={{ marginTop: 34 }}>{copy.pricingTitle}</h2>

          <div style={{ marginTop: 14 }}>
            <CostsTabs
              items={copy.pricingLines.map((line) => {
                const idx = line.indexOf(":");
                const title = idx > -1 ? line.slice(0, idx).trim() : optionLabel;
                const text = idx > -1 ? line.slice(idx + 1).trim() : line;
                return { title, text };
              })}
            />
          </div>

          <h2 style={{ marginTop: 34 }}>{copy.faqTitle}</h2>

          <div className="faq" style={{ marginTop: 14 }}>
            {copy.faq.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <div className="muted" style={{ marginTop: 8 }}>
                  {item.a}
                </div>
              </details>
            ))}
          </div>

          <div style={{ marginTop: 28 }}>
            <a href={CTA_HREF} className="link-button">
              {copy.ctaLabel}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};